import { AuthBaseClient } from '../base-client';
import type {
  PlatformWhoami,
  PlatformOrgUser,
  PlatformUser,
  PlatformOrganizationsRequest,
  PlatformOrganizationsResponse,
  StartImpersonationRequest,
  StartImpersonationResponse,
  StopImpersonationResponse,
  CreatePlatformUserRequest,
  PlatformConfig,
} from './types';

/**
 * Client for the `/api/platform/*` routes. Instantiated once in the SPA
 * (same pattern as other domain clients). All calls require the caller to
 * be a platform principal; the API returns 403 for org users.
 */
export class PlatformClient extends AuthBaseClient {
  async whoami(signal?: AbortSignal): Promise<PlatformWhoami> {
    return this.get<PlatformWhoami>('/api/platform/whoami', signal);
  }

  async getConfig(signal?: AbortSignal): Promise<PlatformConfig> {
    return this.get<PlatformConfig>('/api/platform/config', signal);
  }

  async listOrganizations(
    req: PlatformOrganizationsRequest = {},
    signal?: AbortSignal
  ): Promise<PlatformOrganizationsResponse> {
    const params = new URLSearchParams();
    if (req.q) params.set('q', req.q);
    if (req.page) params.set('page', String(req.page));
    if (req.pageSize) params.set('pageSize', String(req.pageSize));
    const qs = params.toString();
    return this.get<PlatformOrganizationsResponse>(
      `/api/platform/organizations${qs ? `?${qs}` : ''}`,
      signal
    );
  }

  async listOrganizationUsers(
    organizationId: string,
    signal?: AbortSignal
  ): Promise<PlatformOrgUser[]> {
    return this.get<PlatformOrgUser[]>(
      `/api/platform/organizations/${organizationId}/users`,
      signal
    );
  }

  async startImpersonation(
    req: StartImpersonationRequest,
    signal?: AbortSignal
  ): Promise<StartImpersonationResponse> {
    return this.post<StartImpersonationResponse>('/api/platform/impersonations', req, signal);
  }

  async stopImpersonation(signal?: AbortSignal): Promise<StopImpersonationResponse> {
    return this.post<StopImpersonationResponse>('/api/platform/impersonations/stop', {}, signal);
  }

  async listPlatformUsers(
    status: 'active' | 'inactive' = 'active',
    signal?: AbortSignal
  ): Promise<PlatformUser[]> {
    return this.get<PlatformUser[]>(`/api/platform/admin/users?status=${status}`, signal);
  }

  async createPlatformUser(
    req: CreatePlatformUserRequest,
    signal?: AbortSignal
  ): Promise<PlatformUser> {
    return this.post<PlatformUser>('/api/platform/admin/users', req, signal);
  }

  async revokePlatformUser(id: string, signal?: AbortSignal): Promise<PlatformUser> {
    return this.delete<PlatformUser>(`/api/platform/admin/users/${id}`, signal);
  }
}
